import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Search } from "lucide-react";
import { DateRangePicker, type DateRange } from "@/components/ui/customs/date-range-picker";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { FilterV2, useFilterV2 } from "@/components/shared/filter-v2";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CompanyReducedItemDto } from "@/types/api/api-types";
import useUrlPagination from "@/hooks/useUrlPagination";
import { useGetCompanyBoListV1 } from "@/api/company.api";

const columnHelper = createColumnHelper<CompanyReducedItemDto>();

const formatBillingEntityName = ({
  companyName,
  internalName,
}: CompanyReducedItemDto["billingEntities"][number]) =>
  internalName && internalName !== companyName ? `${companyName} (${internalName})` : companyName;

const useClientsColumns = () => {
  const { t } = useTranslation("boClients");
  return useMemo(
    () => [
      columnHelper.accessor("businessName", {
        header: t("clientsTable.columns.client"),
        enableSorting: false,
        cell: (info) => <span className="font-medium text-gray-900">{info.getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.billingEntities.map(formatBillingEntityName).join(", "), {
        id: "internalName",
        header: t("clientsTable.columns.internalName"),
        enableSorting: false,
        cell: ({ row }) => {
          const labels = row.original.billingEntities.map(formatBillingEntityName);
          if (labels.length === 0) return <span className="text-gray-400">—</span>;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-2">
                    {labels[0]}
                    {labels.length > 1 && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        +{labels.length - 1}
                      </span>
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <ul className="flex flex-col gap-1">
                    {labels.map((label, index) => (
                      <li key={index}>{label}</li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      }),
      columnHelper.accessor("createdAt", {
        header: t("clientsTable.columns.createdAt"),
        enableSorting: true,
        cell: (info) => format(new Date(info.getValue()), "dd/MM/yyyy"),
      }),
    ],
    [t],
  );
};

type FilterProps = {
  searchField: string;
  setSearchField: React.Dispatch<React.SetStateAction<string>>;
  dateRange: DateRange | null;
  onDateRangeChange: (range: DateRange | null) => void;
  onReset: () => void;
};

const ClientsPageFilter = ({
  searchField,
  setSearchField,
  dateRange,
  onDateRangeChange,
  onReset,
}: FilterProps) => {
  const { t } = useTranslation("boClients");
  const { isOpen, toggle } = useFilterV2();

  return (
    <FilterV2 isOpen={isOpen} onToggle={toggle} onReset={onReset} className="w-full">
      <UnifiedInput
        inputType="text"
        leftElement={<Search />}
        value={searchField}
        onChange={(value) => setSearchField(String(value ?? ""))}
        placeholder={t("clientsFilters.searchPlaceholder")}
        wrapperClassName="w-full max-w-[400px] h-[2.4rem]"
      />
      <DateRangePicker
        placeholder={t("clientsFilters.createdAtPlaceholder")}
        value={dateRange}
        onChange={onDateRangeChange}
        className="min-w-[200px]"
      />
    </FilterV2>
  );
};

export const ClientsPage = () => {
  const { t } = useTranslation("boClients");
  const columns = useClientsColumns();
  const navigate = useNavigate();
  const [searchField, setSearchField] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [pagination, setPagination] = useUrlPagination("boClient");
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleResetFilters = () => {
    setSearchField("");
    setDateRange(null);
  };

  const handleSortingChange: React.Dispatch<React.SetStateAction<SortingState>> = (updater) => {
    setSorting(updater);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const { data: companies } = useGetCompanyBoListV1({
    search: searchField ? searchField : undefined,
    createdAfter: dateRange ? dateRange[0].toISOString() : undefined,
    createdBefore: dateRange ? dateRange[1].toISOString() : undefined,
    orderByCreatedAt: sorting[0] ? (sorting[0].desc ? "desc" : "asc") : undefined,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
  });

  return (
    <div className="flex h-fit w-full flex-1 flex-col gap-4 p-8">
      <div className="flex w-full flex-col gap-2">
        <h1 className="font-jakarta-sans text-3xl font-semibold">{t("clientsPage.title")}</h1>
        <p>{t("clientsPage.subtitle")}</p>
      </div>
      <div className="flex flex-1 flex-col gap-6">
        <div className="flex w-full flex-row justify-between gap-2">
          <ClientsPageFilter
            searchField={searchField}
            setSearchField={setSearchField}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onReset={handleResetFilters}
          />
        </div>
        <DataTable
          data={companies?.data ?? []}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination}
          rowCount={companies?.count ?? 0}
          sorting={sorting}
          setSorting={handleSortingChange}
          onRowClick={(row) => navigate(`/admin/clients/${row.id}`)}
        />
      </div>
    </div>
  );
};
